import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Home, Building2, Hammer, Wrench, ArrowRight, 
  Phone, Mail, MapPin, ChevronRight, ChevronDown, Menu, X, Play, Star, Quote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";
import { LanguageToggle } from "@/components/LanguageToggle";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";

const BeforeAfterSection = () => {
  const { t } = useLang();
  return (
    <section className="py-24 bg-white" data-testid="before-after-section">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-md w-full mx-auto lg:mx-0"
        >
          <BeforeAfterSlider
            before="/images/bathp1.webp"
            after="/images/bathp8.webp"
            beforeLabel={t("Before", "Antes")}
            afterLabel={t("After", "Después")}
            alt={t("Tub and wall panel bathroom remodel", "Remodelación de baño con tina y paneles")}
            sliderLabel={t("Drag to compare before and after", "Deslice para comparar el antes y el después")}
          />
        </motion.div>
        <div>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-emerald-600 mb-2">{t("Before & After", "Antes y Después")}</p>
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 tracking-tight uppercase font-['Archivo'] mb-6">
            {t("Drag to See", "Deslice Para Ver")}<br />{t("The Difference", "La Diferencia")}
          </h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            {t(
              "This bathroom had damaged walls, an old tub surround and water damage under the floor. We repaired the floor, replaced the drain plumbing, and installed a new tub with seamless wall panels, a handheld shower and new vinyl plank flooring.",
              "Este baño tenía paredes dañadas, un forro de tina viejo y daño por agua debajo del piso. Reparamos el piso, cambiamos la plomería del drenaje e instalamos una tina nueva con paneles de pared, regadera de mano y piso nuevo de vinil."
            )}
          </p>
          <p className="text-sm text-slate-500 mb-8">{t("Move the slider left and right to compare.", "Mueva la barra a la izquierda y a la derecha para comparar.")}</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/gallery/bathroom">
              <Button variant="outline" className="border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white rounded-sm px-8 py-4 font-bold tracking-wide transition-colors duration-300 uppercase text-sm" data-testid="before-after-gallery-btn">
                {t("See the Full Process", "Ver Todo el Proceso")}
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="bg-emerald-600 text-white hover:bg-blue-900 rounded-sm px-8 py-4 font-bold tracking-wide transition-colors duration-300 uppercase text-sm" data-testid="before-after-quote-btn">
                {t("Get a Quote", "Cotizar")} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const galleryLinks = [
  { path: "/gallery/videos", en: "Videos", es: "Videos" },
  { path: "/gallery/bathroom", en: "Bathroom Remodeling", es: "Remodelación de Baños" },
  { path: "/gallery/kitchen", en: "Kitchen Remodeling", es: "Remodelación de Cocinas" },
  { path: "/gallery/flooring", en: "Flooring & Tile", es: "Pisos y Azulejo" },
  { path: "/gallery/painting", en: "Painting", es: "Pintura" },
  { path: "/gallery/handyman", en: "Handyman", es: "Reparaciones Generales" }
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLang();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img 
            src="https://customer-assets.emergentagent.com/job_site-snap/artifacts/6rsrhuvq_FT%20Construction%20original072025001.jpg" 
            alt="Finix Terra Construction LLC" 
            className="h-16 w-auto object-contain"
          />
        </Link>
        
        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className="nav-link text-sm font-medium text-slate-700 hover:text-emerald-600">{t("Home", "Inicio")}</Link>
          <Link to="/services" className="nav-link text-sm font-medium text-slate-700 hover:text-emerald-600">{t("Services", "Servicios")}</Link>
          <div className="relative group">
            <Link to="/gallery" className="nav-link text-sm font-medium text-slate-700 hover:text-emerald-600 inline-flex items-center gap-1" data-testid="nav-gallery">
              {t("Gallery", "Galería")}
              <ChevronDown className="w-3 h-3" />
            </Link>
            <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="bg-white border border-slate-200 shadow-xl rounded-sm w-60 py-2" data-testid="gallery-dropdown">
                <Link to="/gallery" className="block px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 font-medium">{t("All Projects", "Todos los Proyectos")}</Link>
                {galleryLinks.map((g, i) => (
                  <div key={g.path}>
                    {i === 1 && <div className="border-t border-slate-100 my-1"></div>}
                    <Link to={g.path} className="block px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700">{t(g)}</Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <Link to="/contact" className="nav-link text-sm font-medium text-slate-700 hover:text-emerald-600">{t("Contact", "Contacto")}</Link>
        </div>

        <div className="hidden md:flex items-center gap-4">
          <LanguageToggle />
          <Link to="/admin" className="text-sm font-medium text-slate-500 hover:text-slate-700">Admin</Link>
          <Link to="/contact">
            <Button className="bg-emerald-600 text-white hover:bg-blue-800 rounded-sm px-6 py-2 font-bold tracking-wide transition-all duration-300 uppercase text-xs" data-testid="get-quote-btn">
              {t("Get Quote", "Cotizar")}
            </Button>
          </Link>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <LanguageToggle testId="mobile-lang-toggle-btn" />
          <button 
            className="p-2" 
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 py-4 px-6 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-3">
            <Link to="/" className="text-sm font-medium text-slate-700" onClick={() => setIsOpen(false)}>{t("Home", "Inicio")}</Link>
            <Link to="/services" className="text-sm font-medium text-slate-700" onClick={() => setIsOpen(false)}>{t("Services", "Servicios")}</Link>
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 mt-2">{t("Gallery", "Galería")}</p>
              <div className="flex flex-col gap-2 pl-2 border-l-2 border-emerald-600">
                <Link to="/gallery" className="text-sm font-medium text-slate-700" onClick={() => setIsOpen(false)}>{t("All Projects", "Todos los Proyectos")}</Link>
                {galleryLinks.map((g) => (
                  <Link key={g.path} to={g.path} className="text-sm text-slate-600" onClick={() => setIsOpen(false)}>{t(g)}</Link>
                ))}
              </div>
            </div>
            <Link to="/contact" className="text-sm font-medium text-slate-700 mt-2" onClick={() => setIsOpen(false)}>{t("Contact", "Contacto")}</Link>
            <Link to="/admin" className="text-sm font-medium text-slate-500" onClick={() => setIsOpen(false)}>Admin</Link>
          </div>
        </div>
      )}
    </nav>
  );
};

const Footer = () => {
  const { t } = useLang();
  return (
  <footer className="bg-blue-900 text-white py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_site-snap/artifacts/6rsrhuvq_FT%20Construction%20original072025001.jpg" 
              alt="Finix Terra Construction LLC" 
              className="h-20 w-auto bg-white rounded p-2"
            />
          </div>
          <p className="text-blue-200 text-sm leading-relaxed max-w-md">
            <span className="block text-white font-semibold mb-1" data-testid="footer-tagline">
              {t("General Contractor — Remodeling & Restoration Excellence Since 2022", "General Contractor — Excelencia en Remodelación y Restauración Desde 2022")}
            </span>
            {t(
              "Finix Terra Construction LLC delivers quality remodeling and restoration services for residential and small commercial properties across Oregon, including kitchen, bathroom, flooring, painting, and handyman projects.",
              "Finix Terra Construction LLC ofrece servicios de remodelación y restauración de calidad para propiedades residenciales y pequeños comercios en todo Oregon, incluyendo cocinas, baños, pisos, pintura y reparaciones generales."
            )}
          </p>
          <p className="text-emerald-400 text-sm italic font-medium mt-3 max-w-md" data-testid="footer-slogan">
            {t("\"Our goal is our clients' satisfaction.\"", "\"Nuestra meta es la satisfacción de nuestros clientes.\"")}
          </p>
        </div>
        
        <div>
          <h4 className="font-bold text-sm uppercase tracking-wider mb-4 font-['Archivo']">{t("Services", "Servicios")}</h4>
          <ul className="space-y-2">
            <li><Link to="/services" className="text-blue-200 text-sm hover:text-emerald-400 transition-colors">{t("Bathroom Remodeling", "Remodelación de Baños")}</Link></li>
            <li><Link to="/services" className="text-blue-200 text-sm hover:text-emerald-400 transition-colors">{t("Kitchen Remodeling", "Remodelación de Cocinas")}</Link></li>
            <li><Link to="/services" className="text-blue-200 text-sm hover:text-emerald-400 transition-colors">{t("Flooring & Tile", "Pisos y Azulejo")}</Link></li>
            <li><Link to="/services" className="text-blue-200 text-sm hover:text-emerald-400 transition-colors">{t("Painting & Handyman", "Pintura y Reparaciones")}</Link></li>
          </ul>
        </div>
        
        <div>
          <h4 className="font-bold text-sm uppercase tracking-wider mb-4 font-['Archivo']">{t("Contact", "Contacto")}</h4>
          <ul className="space-y-3">
            <li className="flex items-center gap-2 text-blue-200 text-sm">
              <Phone className="w-4 h-4 text-emerald-400" />
              (503) 991-7412
            </li>
            <li className="flex items-center gap-2 text-blue-200 text-sm">
              <Mail className="w-4 h-4 text-emerald-400" />
              finixsterra@gmail.com
            </li>
            <li className="flex items-start gap-2 text-blue-200 text-sm">
              <MapPin className="w-4 h-4 text-emerald-400 mt-0.5" />
              Salem, Oregon
            </li>
          </ul>
        </div>
      </div>
      
      <div className="border-t border-blue-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-blue-300 text-xs">© 2026 Finix Terra Construction LLC. {t("All rights reserved.", "Todos los derechos reservados.")}</p>
        <p className="text-blue-400 text-[10px] mt-2 opacity-75" data-testid="music-credit">
          {t("Featured video music:", "Música del video destacado:")} "Motivation Mode" by <a href="https://mixaund.bandcamp.com" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 underline">Mixaund</a> — <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 underline">CC BY 4.0</a>. Royalty free music by <a href="https://www.free-stock-music.com" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 underline">free-stock-music.com</a>.
        </p>
        <div className="flex gap-6">
          <Link to="#" className="text-blue-300 text-xs hover:text-emerald-400">{t("Privacy Policy", "Política de Privacidad")}</Link>
          <Link to="#" className="text-blue-300 text-xs hover:text-emerald-400">{t("Terms of Service", "Términos de Servicio")}</Link>
        </div>
      </div>
    </div>
  </footer>
  );
};

const HeroSection = () => {
  const { t } = useLang();
  return (
  <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
    <div 
      className="absolute inset-0 z-0 bg-slate-800"
    >
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_site-snap/artifacts/6rsrhuvq_FT%20Construction%20original072025001.jpg')`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-slate-800/30 to-slate-900/80"></div>
    </div>
    
    <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
      <div className="max-w-2xl">
        <motion.p 
          className="font-mono text-xs tracking-[0.2em] uppercase text-emerald-400 mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {t("General Contractor · Remodeling & Restoration", "General Contractor · Remodelación y Restauración")}
        </motion.p>
        
        <motion.h1 
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight uppercase leading-none mb-6 font-['Archivo']"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          Finix Terra<br />
          <span className="text-emerald-400">Construction</span><br />
          LLC
        </motion.h1>
        
        <motion.p 
          className="text-slate-300 text-base md:text-lg leading-relaxed mb-8 max-w-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {t(
            "Interior and exterior remodeling services. From residential homes to commercial projects, we deliver quality craftsmanship you can trust.",
            "Servicios de remodelación interior y exterior. Desde casas residenciales hasta proyectos comerciales, entregamos un trabajo de calidad en el que puede confiar."
          )}
        </motion.p>
        
        <motion.div 
          className="flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Link to="/contact">
            <Button className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm px-8 py-4 font-bold tracking-wide transition-all duration-300 uppercase text-sm" data-testid="start-project-btn">
              {t("Start Your Project", "Inicie Su Proyecto")} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          <Link to="/gallery">
            <Button variant="outline" className="border-white text-white hover:bg-white hover:text-blue-900 rounded-sm px-8 py-4 font-bold tracking-wide transition-all duration-300 uppercase text-sm" data-testid="view-gallery-btn">
              {t("View Gallery", "Ver Galería")}
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
    
    {/* Stats bar removed for cleaner look */}
  </section>
  );
};

const ServicesSection = () => {
  const { t } = useLang();
  const services = [
    {
      icon: Home,
      slug: "bathroom",
      title: { en: "Bathroom Remodeling", es: "Remodelación de Baños" },
      description: {
        en: "Complete bathroom renovations including tubs, showers, vanities, and tile work. Transform your bathroom into a modern oasis.",
        es: "Renovaciones completas de baños incluyendo tinas, regaderas, lavabos y azulejo. Convierta su baño en un oasis moderno."
      }
    },
    {
      icon: Building2,
      slug: "kitchen",
      title: { en: "Kitchen Remodeling", es: "Remodelación de Cocinas" },
      description: {
        en: "Full kitchen renovations with custom cabinets, countertops, and modern appliances. Create your dream kitchen.",
        es: "Renovaciones completas de cocinas con gabinetes a la medida, cubiertas y electrodomésticos modernos. Cree la cocina de sus sueños."
      }
    },
    {
      icon: Hammer,
      slug: "flooring",
      title: { en: "Flooring & Tile", es: "Pisos y Azulejo" },
      description: {
        en: "Professional flooring installation including hardwood, laminate, vinyl, and ceramic tile for any room in your home.",
        es: "Instalación profesional de pisos de madera, laminado, vinil y azulejo cerámico para cualquier cuarto de su casa."
      }
    },
    {
      icon: Wrench,
      slug: "painting",
      title: { en: "Painting & Handyman", es: "Pintura y Reparaciones" },
      description: {
        en: "Interior and exterior painting, plus handyman services: faucet replacement, electrical, plumbing, windows and doors.",
        es: "Pintura interior y exterior, además de reparaciones generales: cambio de llaves de agua, electricidad, plomería, ventanas y puertas."
      }
    }
  ];

  return (
    <section className="py-24 bg-slate-50 noise-bg" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-emerald-600 mb-2">{t("What We Do", "Lo Que Hacemos")}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 tracking-tight uppercase font-['Archivo']">
              {t("Our Services", "Nuestros Servicios")}
            </h2>
          </div>
          <Link to="/services" className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-emerald-600 transition-colors">
            {t("View All Services", "Ver Todos los Servicios")} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, i) => (
            <motion.div 
              key={i}
              className="bg-white border border-slate-200 p-6 relative overflow-hidden group service-card card-marker"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`service-card-${service.title.en.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-12 h-12 bg-blue-900 flex items-center justify-center mb-4">
                <service.icon className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="font-bold text-lg text-blue-900 mb-2 font-['Archivo'] uppercase tracking-wide">{t(service.title)}</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{t(service.description)}</p>
              <Link 
                to={`/services#${service.slug}`}
                className="inline-flex items-center text-xs font-medium text-emerald-600 hover:text-emerald-700 uppercase tracking-wider"
                data-testid={`learn-more-${service.slug}`}
              >
                {t("Learn More", "Ver Más")} <ArrowRight className="w-3 h-3 ml-1" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const VideoShowcaseSection = () => {
  const { t } = useLang();
  return (
  <section className="py-24 bg-slate-50 noise-bg" data-testid="video-showcase-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-emerald-600 mb-2">{t("See It In Action", "Véalo en Acción")}</p>
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 tracking-tight uppercase font-['Archivo']">
            {t("Watch Our", "Vea Nuestro")}<br/>{t("Craftsmanship", "Trabajo")}
          </h2>
        </div>
      </div>

      <motion.div 
        className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="lg:col-span-3 relative bg-blue-900 overflow-hidden shadow-2xl border-2 border-blue-900">
          <video 
            controls
            playsInline
            preload="metadata"
            poster="/videos/project-video-poster.jpg"
            className="w-full h-auto max-h-[600px] object-contain bg-black"
            data-testid="landing-featured-video"
          >
            <source src="/videos/project-video.mp4" type="video/mp4" />
            {t("Your browser does not support video playback.", "Su navegador no puede reproducir este video.")}
          </video>
        </div>
        <div className="lg:col-span-2 lg:pl-6">
          <div className="inline-flex items-center gap-2 bg-emerald-600 text-white px-3 py-1.5 mb-4">
            <Play className="w-3 h-3 fill-white" />
            <span className="font-mono text-xs tracking-wider uppercase">{t("Featured Project", "Proyecto Destacado")}</span>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-blue-900 mb-4 font-['Archivo'] uppercase leading-tight">
            {t("Real Work, Real Results", "Trabajo Real, Resultados Reales")}
          </h3>
          <p className="text-slate-600 leading-relaxed mb-6">
            {t(
              "Take a look behind the scenes at one of our recent renovation projects. From demolition to the final finishing touches, every detail reflects the craftsmanship we bring to your home.",
              "Vea detrás de cámaras uno de nuestros proyectos de renovación más recientes. Desde la demolición hasta los últimos detalles, cada parte refleja la calidad que llevamos a su hogar."
            )}
          </p>
          <Link to="/gallery/videos">
            <Button className="bg-blue-900 text-white hover:bg-emerald-600 rounded-sm px-8 py-4 font-bold tracking-wide transition-all duration-300 uppercase text-sm" data-testid="video-cta-btn">
              {t("Watch More Videos", "Ver Más Videos")} <Play className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
  );
};

const testimonials = [
  {
    name: "Jim Mackey",
    badge: "Local Guide",
    rating: 5,
    date: "Feb 2024",
    text: "We used Finix Terra for a bathroom remodel and couldn't be more satisfied. Francisco (the owner) did the vast majority of the work himself and the quality speaks for itself.",
    project: "Bathroom Remodel"
  },
  {
    name: "Cody Layton",
    rating: 5,
    date: "2024",
    text: "Working with Finix Terra was an absolute pleasure. My wife and I just bought our first home and Francisco delivered great work at a reasonable price.",
    project: "First Home Renovation"
  },
  {
    name: "Nora (4310Durbin)",
    rating: 5,
    date: "Nov 2024",
    text: "I recently hired Finix Terra Construction to paint the interior of my home, and I couldn't be more impressed with the results. Highly recommend!",
    project: "Interior Painting"
  },
  {
    name: "Nelda Kerns",
    rating: 5,
    date: "Jun 2024",
    text: "Wonderful service by Franco and crew — from a bid that was well done and complete to a job well done on the water system. He came back in pouring rain to explain the system to this old lady.",
    project: "Water System Install"
  },
  {
    name: "Dionisia Leal",
    rating: 5,
    date: "2024",
    text: "I am new to Salem and I was very lucky because Francisco was recommended and he did a great job. I had a small leak and he handled it perfectly. Great price!",
    project: "Leak Repair"
  },
  {
    name: "Nick Bradley",
    badge: "Local Guide",
    rating: 5,
    date: "2024",
    text: "Francisco does top notch work at a good price.",
    project: "General Contractor"
  },
  {
    name: "John",
    rating: 5,
    date: "2024",
    text: "Francisco came to our house on time to provide an estimate for some crawl space work to replace a dryer vent pipe. Professional and reliable from start to finish.",
    project: "Crawl Space Work"
  },
  {
    name: "Katie Mackor",
    rating: 5,
    date: "2026",
    text: "I hired Francisco to fix a leak in the front of my house. Excellent work, highly recommend!",
    project: "Leak Repair"
  }
];

const TestimonialsSection = () => {
  const { t, lang } = useLang();
  return (
  <section className="py-24 bg-blue-900 relative overflow-hidden" data-testid="testimonials-section">
    <div className="absolute inset-0 grid-bg opacity-5"></div>
    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
        <div>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-emerald-400 mb-2">{t("Verified Reviews", "Reseñas Verificadas")}</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight uppercase font-['Archivo']">
            {t("What Our Clients", "Lo Que Dicen")}<br/>{t("Are Saying", "Nuestros Clientes")}
          </h2>
          {lang === "es" && <span className="block text-blue-300 text-xs mt-3" data-testid="reviews-language-note">Reseñas originales de Google (en inglés)</span>}
        </div>
        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 px-5 py-3 rounded-sm">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-emerald-400 text-emerald-400" />
            ))}
          </div>
          <div className="text-white">
            <p className="font-bold text-lg leading-none">5.0</p>
            <p className="text-xs text-blue-200">{t("on Google", "en Google")}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {testimonials.map((t, i) => (
          <motion.div 
            key={i}
            className="bg-blue-800/50 backdrop-blur-sm border border-blue-700 p-7 relative group hover:border-emerald-500 transition-colors"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
            data-testid={`testimonial-${i}`}
          >
            <Quote className="absolute top-5 right-5 w-8 h-8 text-emerald-400/20 group-hover:text-emerald-400/40 transition-colors" />
            <div className="flex items-center gap-1 mb-4">
              {[...Array(t.rating)].map((_, j) => (
                <Star key={j} className="w-4 h-4 fill-emerald-400 text-emerald-400" />
              ))}
            </div>
            <p className="text-blue-50 text-sm leading-relaxed mb-6 relative z-10">
              "{t.text}"
            </p>
            <div className="flex items-center gap-3 pt-5 border-t border-blue-700/50">
              <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-base">{t.name.charAt(0)}</span>
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-white text-sm truncate">{t.name}</p>
                <div className="flex items-center gap-2 text-xs text-blue-300">
                  {t.badge && (
                    <span className="bg-emerald-600/30 text-emerald-300 px-1.5 py-0.5 text-[10px] uppercase tracking-wider">
                      {t.badge}
                    </span>
                  )}
                  <span>{t.date}</span>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <a 
          href="https://maps.app.goo.gl/hUEuegGVDtCrpEKW6" 
          target="_blank" 
          rel="noopener noreferrer"
          data-testid="google-reviews-link"
        >
          <Button className="bg-white text-blue-900 hover:bg-emerald-600 hover:text-white rounded-sm px-8 py-4 font-bold tracking-wide transition-all duration-300 uppercase text-sm">
            {t("Read More Reviews on Google", "Leer Más Reseñas en Google")} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </a>
      </div>
    </div>
  </section>
  );
};

const CTASection = () => {
  const { t } = useLang();
  return (
  <section className="py-24 bg-emerald-600 noise-bg relative" data-testid="cta-section">
    <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
      <img 
        src="https://customer-assets.emergentagent.com/job_site-snap/artifacts/6rsrhuvq_FT%20Construction%20original072025001.jpg" 
        alt="Finix Terra" 
        className="h-20 w-auto mx-auto mb-6 bg-white rounded-lg p-2"
      />
      <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight uppercase mb-6 font-['Archivo']">
        {t("Ready to Start Your Project?", "¿Listo Para Iniciar Su Proyecto?")}
      </h2>
      <p className="text-emerald-100 text-base md:text-lg mb-8 max-w-2xl mx-auto">
        {t(
          "Get a free consultation and quote for your remodeling or restoration project. Our team is ready to bring your vision to life.",
          "Reciba una consulta y cotización gratis para su proyecto de remodelación o restauración. Nuestro equipo está listo para hacer realidad su visión."
        )}
      </p>
      <Link to="/contact">
        <Button className="bg-white text-emerald-600 hover:bg-blue-900 hover:text-white rounded-sm px-10 py-4 font-bold tracking-wide transition-all duration-300 uppercase text-sm" data-testid="request-quote-btn">
          {t("Request a Quote", "Pedir Cotización")} <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </Link>
    </div>
  </section>
  );
};

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      <Navigation />
      <HeroSection />
      <ServicesSection />
      <BeforeAfterSection />
      <VideoShowcaseSection />
      <TestimonialsSection />
      <CTASection />
      <Footer />
    </div>
  );
}

export { Navigation, Footer };
